import { SettingsList } from './SettingsList';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { agentElevationOptions, approveAgentElevation, closeAgentElevation, getAgentElevations } from '../lib/api';
import { signWithKey } from '../lib/webauthn';
import { absolute } from '../lib/time';
import { useScope } from '../scope/scope';
import { Button } from '../ui/Button';
import { useToast } from '../ui/toast';

/** Owners approve the requesting agent's exact scope, never a generic owner session. */
export function AgentElevations() {
  const { mspId } = useScope();
  const client = useQueryClient();
  const toast = useToast();
  const key = ['scope', mspId, 'agent-elevations'];
  const list = useQuery({ queryKey: key, queryFn: ({ signal }) => getAgentElevations(mspId, signal), refetchInterval: 15_000 });
  const approve = useMutation({
    mutationFn: async (id: string) => approveAgentElevation(mspId, id, await signWithKey(await agentElevationOptions(mspId, id))),
    onSuccess: () => { void client.invalidateQueries({ queryKey: key }); toast({ title: 'Temporary access approved', body: 'It expires after 15 minutes. You can revoke it here sooner.' }); },
    onError: () => toast({ tone: 'warning', title: 'Access wasn’t approved', body: 'Only the owner who issued this credential can approve it. Touch your key when asked; an expired request needs a new request.' }),
  });
  const close = useMutation({
    mutationFn: (id: string) => closeAgentElevation(mspId, id),
    onSuccess: rows => client.setQueryData(key, rows),
    onError: () => toast({ tone: 'warning', title: 'Access couldn’t be closed', body: 'Refresh and try again.' }),
  });
  return <section className="space-y-2" aria-label="Temporary agent access">
    <p className="text-[12.5px] text-ink-3">An agent can request access to change automatic agent updates for named customers. Its issuing owner approves with a key touch, for 15 minutes. This does not approve device jobs.</p>
    {list.isPending && <p className="text-[13px] text-ink-3">Loading requests…</p>}
    {list.isError && <p role="alert" className="text-[13px] text-ink-3">Temporary access couldn’t be loaded.</p>}
    {list.data?.length === 0 && <p className="text-[13px] text-ink-3">No recent requests.</p>}
    <SettingsList id="agent-elevations" title="Temporary agent access" items={list.data ?? []} newest={g => g.createdAt}>{visible => <ul className="divide-y divide-line rounded-lg border border-line">
      {visible.map(g => <li key={g.id} className="space-y-1 px-3 py-2 text-[13px]">
        <p className="font-medium">{g.agentName} · {g.status}</p>
        <p>Change automatic agent updates: {g.customers.map(c => c.name).join(', ')}</p>
        <p className="text-ink-2">{g.reason}</p>
        <p className="text-[12.5px] text-ink-3">{g.status === 'approved' && g.expiresAt ? `Access expires ${absolute(g.expiresAt)}`
          : `Request expires ${absolute(g.requestExpiresAt)}`}</p>
        {g.status === 'pending' && g.canApprove && <Button size="sm" disabled={approve.isPending || close.isPending}
          onClick={() => approve.mutate(g.id)}>{approve.isPending ? 'Touch your key…' : 'Approve for 15 minutes'}</Button>}
        {g.status === 'pending' && !g.canApprove && <p className="text-ink-3">Only its issuing owner can approve this request.</p>}
        {['pending', 'approved'].includes(g.status) && <Button size="sm" variant="secondary" disabled={close.isPending || approve.isPending}
          onClick={() => close.mutate(g.id)}>{g.status === 'pending' ? 'Decline request' : 'Revoke access'}</Button>}
      </li>)}
    </ul>}</SettingsList>
  </section>;
}
